import { useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * IntegrationsStatusCard — admin-visible status strip for every external
 * integration (LLM, Public.com broker, Twilio SMS, Resend email).
 *
 * Polls /api/omega/integrations/status every 30s. Each row shows
 * configured + healthy + a human detail string. A status-dot + color coding
 * tells the operator at a glance whether the integration is live.
 */

const ICONS = {
  llm: "Ψ",
  public: "◎",
  twilio: "✎",
  resend: "✉",
};
const ACCENTS = {
  llm: "#a78bfa",
  public: "#4ade80",
  twilio: "#60a5fa",
  resend: "#22d3ee",
};
const ORDER = ["llm", "public", "twilio", "resend"];

export default function IntegrationsStatusCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [pushResult, setPushResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/omega/integrations/status`);
      setData(r.data);
    } catch {} finally { setLoading(false); }
  }, []);

  const firePush = async () => {
    setPushing(true);
    setPushResult(null);
    try {
      const r = await axios.post(`${API}/omega/tea/push`, {});
      setPushResult({
        ok: !!r.data?.ok,
        pushed: r.data?.pushed ?? 0,
        skipped: r.data?.skipped_dup ?? 0,
        held: (r.data?.held_tickers || []).slice(0, 6).join(", ") || "none",
      });
    } catch (e) {
      setPushResult({ ok: false, error: "request failed" });
    } finally { setPushing(false); }
    setTimeout(() => setPushResult(null), 8000);
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const rows = data?.integrations || {};
  const total = ORDER.length;
  const healthy = ORDER.filter(k => rows[k]?.healthy).length;

  return (
    <div className="card" data-testid="integrations-status-card" style={{
      borderColor: "rgba(96,165,250,0.26)",
      background: "linear-gradient(165deg, rgba(96,165,250,0.04), rgba(16,185,129,0.02))",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 9, color: "var(--dim)", letterSpacing: 2, fontWeight: 700 }}>INTEGRATIONS</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text)" }}>
            {loading ? "Checking…" : `${healthy}/${total} integrations live`}
          </div>
        </div>
        <div data-testid="integrations-overall-pill" style={{
          fontSize: 9, fontWeight: 800, letterSpacing: 1.5,
          padding: "5px 11px", borderRadius: 999,
          color: healthy === total ? "#4ade80" : healthy >= 2 ? "#fbbf24" : "#f87171",
          background: healthy === total ? "rgba(74,222,128,0.12)" : healthy >= 2 ? "rgba(251,191,36,0.12)" : "rgba(248,113,113,0.12)",
          border: healthy === total ? "1px solid rgba(74,222,128,0.36)" : healthy >= 2 ? "1px solid rgba(251,191,36,0.36)" : "1px solid rgba(248,113,113,0.36)",
        }}>{healthy === total ? "ALL LIVE" : healthy >= 2 ? "PARTIAL" : "LIMITED"}</div>
      </div>

      <div style={{ display: "grid", gap: 8 }}>
        {ORDER.map(key => {
          const row = rows[key];
          const accent = ACCENTS[key];
          return (
            <div key={key} data-testid={`integration-${key}`} style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "11px 13px", borderRadius: 11,
              background: "rgba(255,255,255,0.025)",
              border: `1px solid ${row?.healthy ? "rgba(255,255,255,0.06)" : row?.configured ? "rgba(251,191,36,0.3)" : "rgba(255,255,255,0.05)"}`,
            }}>
              <div style={{
                width: 30, height: 30, borderRadius: 9, flex: "0 0 auto",
                background: `${accent}1A`, color: accent,
                display: "grid", placeItems: "center", fontSize: 15, fontWeight: 700,
              }}>{ICONS[key]}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 1 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text)" }}>{row?.label || key}</span>
                  <span data-testid={`integration-${key}-state`} style={{
                    fontSize: 8, fontWeight: 800, letterSpacing: 1.2,
                    padding: "2px 6px", borderRadius: 4,
                    background: row?.healthy ? "rgba(74,222,128,0.16)" : row?.configured ? "rgba(251,191,36,0.12)" : "rgba(255,255,255,0.06)",
                    color: row?.healthy ? "#4ade80" : row?.configured ? "#fbbf24" : "var(--dim)",
                    border: `1px solid ${row?.healthy ? "rgba(74,222,128,0.32)" : row?.configured ? "rgba(251,191,36,0.32)" : "rgba(255,255,255,0.1)"}`,
                  }}>{row?.healthy ? "LIVE" : row?.configured ? "NEEDS SETUP" : "OFF"}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--dim)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row?.detail || (loading ? "checking…" : "—")}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button
          data-testid="tea-push-fire"
          onClick={firePush}
          disabled={pushing}
          style={{
            padding: "8px 14px", borderRadius: 999, border: "1px solid rgba(167,139,250,0.4)",
            background: pushing ? "rgba(167,139,250,0.18)" : "rgba(167,139,250,0.10)",
            color: "#a78bfa", fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
            cursor: pushing ? "wait" : "pointer", fontFamily: "inherit",
            display: "inline-flex", alignItems: "center", gap: 6,
          }}
        >☕ {pushing ? "Brewing…" : "Test-fire Tea push"}</button>
        {pushResult && (
          <span data-testid="tea-push-result" style={{
            fontSize: 11, fontWeight: 600,
            color: pushResult.ok ? "#4ade80" : "#f87171",
          }}>
            {pushResult.ok
              ? `✓ ${pushResult.pushed} pushed · ${pushResult.skipped} deduped · held: ${pushResult.held}`
              : `✕ ${pushResult.error || "push failed"}`}
          </span>
        )}
      </div>

      <div style={{ marginTop: 10, fontSize: 10, color: "var(--dim)", textAlign: "center", letterSpacing: 0.5 }}>
        auto-refreshes every 30s
      </div>
    </div>
  );
}
